import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import ExportButton from '../../components/ExportButton';
import { CheckCircle, XCircle, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SHIFT_OPTIONS = [1, 1.25, 1.5, 1.75, 2];
const DEFAULT_SHIFT = 1.5;

const ATTENDANCE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'emp_id', label: 'Emp/Contract ID' },
  { key: 'halt_location', label: 'Halt Location' },
  { key: 'type', label: 'Type', format: r => (r.type || 'own') === 'contract' ? 'Contract' : 'Own' },
  { key: 'status', label: 'Status', format: r => (r.status || 'present').toUpperCase() },
  { key: 'shifts', label: 'Shifts', format: r => r.status === 'absent' ? 0 : (r.shifts ?? DEFAULT_SHIFT) },
  { key: 'remarks', label: 'Remarks' },
];

export default function LoadmanAttendance() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/loadman-attendance', { params: { date } });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const updateRow = (loadmanId, patch) => {
    setRows(prev => prev.map(r => r.loadman_id === loadmanId ? { ...r, ...patch } : r));
  };

  const setStatus = (loadmanId, status) => {
    updateRow(loadmanId, { status, shifts: status === 'absent' ? 0 : DEFAULT_SHIFT });
  };

  const setShift = (loadmanId, shifts) => {
    updateRow(loadmanId, { shifts, status: 'present' });
  };

  const markAll = (status) => {
    setRows(prev => prev.map(r => ({ ...r, status, shifts: status === 'absent' ? 0 : DEFAULT_SHIFT })));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        date,
        records: rows.map(r => ({
          loadman_id: r.loadman_id,
          status: r.status,
          shifts: r.status === 'absent' ? 0 : (r.shifts ?? DEFAULT_SHIFT),
          remarks: r.remarks || '',
        })),
      };
      const { data } = await api.put('/admin/loadman-attendance', payload);
      toast.success(`Saved ${data.count} records for ${date}`);
      fetchAttendance();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.emp_id || '').toLowerCase().includes(q) ||
        (r.halt_location || '').toLowerCase().includes(q)
      )
    : rows;

  const presentCount = rows.filter(r => r.status === 'present').length;
  const absentCount = rows.length - presentCount;
  const totalShifts = rows.reduce((a, r) => a + (r.status === 'absent' ? 0 : (r.shifts ?? 0)), 0);

  return (
    <div data-testid="loadman-attendance-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Helper Attendance</h1>
        <p className="text-[#666] text-sm mt-1">
          Manual entry by admin. Default: Present, {DEFAULT_SHIFT} shifts. Absent is 0 shifts.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white border border-[#E5E5E5] p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest">Date</Label>
          <Input
            data-testid="la-date-input"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="rounded-none"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest">Search</Label>
          <Input
            data-testid="la-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Name, Emp ID or halt..."
            className="rounded-none"
          />
        </div>
        <Button
          data-testid="la-mark-all-present"
          variant="outline"
          className="rounded-none"
          onClick={() => markAll('present')}
        >
          Mark All Present
        </Button>
        <Button
          data-testid="la-mark-all-absent"
          variant="outline"
          className="rounded-none text-[#FF3333] border-[#FF3333]/30"
          onClick={() => markAll('absent')}
        >
          Mark All Absent
        </Button>
      </div>

      {/* Summary + actions */}
      <div className="bg-white border border-[#E5E5E5] p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-4 text-sm">
          <span><span className="text-[#666]">Total</span> <strong>{rows.length}</strong></span>
          <span><span className="text-[#00C853]">Present</span> <strong>{presentCount}</strong></span>
          <span><span className="text-[#FF3333]">Absent</span> <strong>{absentCount}</strong></span>
          <span><span className="text-[#666]">Total Shifts</span> <strong>{totalShifts.toFixed(2)}</strong></span>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton rows={filtered} columns={ATTENDANCE_COLUMNS} filename={`loadman_attendance_${date}`} testId="export-loadman-attendance-btn" />
          <Button
            data-testid="la-save-btn"
            onClick={save}
            disabled={saving || loading || rows.length === 0}
            className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none gap-2"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? 'Saving...' : 'Save Attendance'}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F3F3F3]">
              <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Emp / Contract ID</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Halt</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-center">Shifts</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r, i) => {
              const isAbsent = r.status === 'absent';
              return (
                <TableRow key={r.loadman_id} className={i % 2 ? 'bg-[#F3F3F3]/30' : ''} data-testid={`la-row-${r.loadman_id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="font-mono text-sm">{r.emp_id || <span className="text-[#999]">—</span>}</TableCell>
                  <TableCell>{r.halt_location || <span className="text-[#999]">—</span>}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${(r.type || 'own') === 'contract' ? 'bg-[#FF8C00]/10 text-[#FF8C00]' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>
                      {(r.type || 'own') === 'contract' ? 'Contract' : 'Own'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex border border-[#E5E5E5] h-8 w-44">
                      <button
                        type="button"
                        onClick={() => setStatus(r.loadman_id, 'present')}
                        data-testid={`la-present-${r.loadman_id}`}
                        className={`flex-1 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest ${r.status === 'present' ? 'bg-[#00C853] text-white' : 'bg-white text-[#666]'}`}
                      >
                        <CheckCircle size={12} weight="bold" /> Present
                      </button>
                      <button
                        type="button"
                        onClick={() => setStatus(r.loadman_id, 'absent')}
                        data-testid={`la-absent-${r.loadman_id}`}
                        className={`flex-1 flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest border-l border-[#E5E5E5] ${isAbsent ? 'bg-[#FF3333] text-white' : 'bg-white text-[#666]'}`}
                      >
                        <XCircle size={12} weight="bold" /> Absent
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <select
                      disabled={isAbsent}
                      value={isAbsent ? 0 : (r.shifts ?? DEFAULT_SHIFT)}
                      onChange={e => setShift(r.loadman_id, parseFloat(e.target.value))}
                      className="border border-[#E5E5E5] px-2 py-1 text-sm bg-white disabled:bg-[#F3F3F3] disabled:text-[#999]"
                      data-testid={`la-shifts-${r.loadman_id}`}
                    >
                      {isAbsent && <option value="0">0</option>}
                      {SHIFT_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={r.remarks || ''}
                      onChange={e => updateRow(r.loadman_id, { remarks: e.target.value })}
                      className="rounded-none h-8 text-sm"
                      placeholder="Optional"
                      data-testid={`la-remarks-${r.loadman_id}`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#666] py-8">
                  {loading ? 'Loading...' : 'No helpers yet — add some from the Helpers master first.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
